'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Specification {
  label: string;
  value: string;
}

interface Certification {
  name: string;
  icon: string;
}

interface CaseStudy {
  title: string;
  description: string;
  outcome: string;
}

interface CapabilityCardData {
  id: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
  specifications: Specification[];
  applications: string[];
  certifications: Certification[];
  caseStudy: CaseStudy;
  capacityStatus: 'high' | 'medium' | 'low';
  downloadUrl: string;
}

interface CapabilityCardProps {
  capability: CapabilityCardData;
  onRequestQuote: (capabilityId: string) => void;
}

const CapabilityCard = ({ capability, onRequestQuote }: CapabilityCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'specs' | 'applications' | 'case-study'>('specs');

  const capacityColors = {
    high: 'text-success',
    medium: 'text-warning',
    low: 'text-error'
  };

  const capacityLabels = {
    high: 'High Availability',
    medium: 'Moderate Availability',
    low: 'Limited Availability'
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden hover:border-accent transition-all duration-300 hover:shadow-aerospace">
      {/* Image Section */}
      <div className="relative h-64 overflow-hidden group">
        <AppImage
          src={capability.image}
          alt={capability.alt}
          className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/50 to-transparent opacity-60"></div>
        
        {/* Capacity Indicator */}
        <div className="absolute top-4 right-4 flex items-center space-x-2 bg-card/90 backdrop-blur-sm px-3 py-1.5 rounded-full border border-border">
          <div className={`w-2 h-2 rounded-full ${capacityColors[capability.capacityStatus]} animate-pulse`}></div>
          <span className={`font-cta text-xs ${capacityColors[capability.capacityStatus]}`}>
            {capacityLabels[capability.capacityStatus]}
          </span>
        </div>

        {/* Icon Overlay */}
        <div className="absolute bottom-4 left-4 w-16 h-16 bg-accent/20 backdrop-blur-md rounded-lg flex items-center justify-center border border-accent/30">
          <Icon name={capability.icon as any} size={32} className="text-accent" />
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6">
        {/* Header */}
        <div className="mb-4">
          <h3 className="font-headline text-2xl font-bold text-foreground mb-2">
            {capability.title}
          </h3>
          <p className="font-body text-sm text-technical-gray leading-relaxed">
            {capability.description}
          </p>
        </div>

        {/* Certifications */}
        <div className="flex flex-wrap gap-2 mb-4">
          {capability.certifications.map((cert, index) => (
            <div
              key={index}
              className="flex items-center space-x-1 px-2 py-1 bg-muted rounded border border-border"
            >
              <Icon name={cert.icon as any} size={14} className="text-aerospace-green" />
              <span className="font-cta text-xs text-technical-gray">{cert.name}</span>
            </div>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-4 bg-muted rounded-lg p-1">
          <button
            onClick={() => setActiveTab('specs')}
            className={`flex-1 px-3 py-2 rounded-md font-cta text-xs transition-all duration-300 ${
              activeTab === 'specs' ?'bg-accent text-primary' :'text-technical-gray hover:text-foreground'
            }`}
          >
            SPECIFICATIONS
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`flex-1 px-3 py-2 rounded-md font-cta text-xs transition-all duration-300 ${
              activeTab === 'applications' ?'bg-accent text-primary' :'text-technical-gray hover:text-foreground'
            }`}
          >
            APPLICATIONS
          </button>
          <button
            onClick={() => setActiveTab('case-study')}
            className={`flex-1 px-3 py-2 rounded-md font-cta text-xs transition-all duration-300 ${
              activeTab === 'case-study' ?'bg-accent text-primary' :'text-technical-gray hover:text-foreground'
            }`}
          >
            CASE STUDY
          </button>
        </div>

        {/* Tab Content */}
        <div className="min-h-[200px]">
          {activeTab === 'specs' && (
            <div className="space-y-3 animate-fade-in">
              {capability.specifications.map((spec, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-border/50">
                  <span className="font-body text-sm text-technical-gray">{spec.label}</span>
                  <span className="font-cta text-sm text-foreground">{spec.value}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'applications' && (
            <div className="space-y-2 animate-fade-in">
              {capability.applications.map((app, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={20} className="text-accent mt-0.5 flex-shrink-0" />
                  <span className="font-body text-sm text-foreground">{app}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'case-study' && (
            <div className="space-y-3 animate-fade-in">
              <div>
                <h4 className="font-headline text-sm font-semibold text-accent mb-2">
                  {capability.caseStudy.title}
                </h4>
                <p className="font-body text-sm text-technical-gray leading-relaxed">
                  {capability.caseStudy.description}
                </p>
              </div>
              <div className="bg-muted rounded-lg p-3 border border-border">
                <span className="font-cta text-xs text-accent uppercase">Outcome</span>
                <p className="font-body text-sm text-foreground mt-1">
                  {capability.caseStudy.outcome}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 mt-6">
          <button
            onClick={() => onRequestQuote(capability.id)}
            className="flex-1 px-4 py-3 bg-mission-orange text-primary font-cta text-sm rounded-lg hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
          >
            REQUEST QUOTE
          </button>
          <a
            href={capability.downloadUrl}
            download
            className="px-4 py-3 bg-muted text-foreground font-cta text-sm rounded-lg hover:bg-accent/10 hover:text-accent transition-all duration-300 flex items-center justify-center"
          >
            <Icon name="ArrowDownTrayIcon" size={20} />
          </a>
        </div>

        {/* Expand Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full mt-4 flex items-center justify-center space-x-2 text-accent hover:text-accent/80 transition-colors duration-300"
        >
          <span className="font-cta text-xs">
            {isExpanded ? 'SHOW LESS' : 'SHOW MORE DETAILS'}
          </span>
          <Icon
            name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            size={16}
            className="transition-transform duration-300"
          />
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-border space-y-4 animate-slide-in-left">
            <div>
              <h4 className="font-headline text-sm font-semibold text-foreground mb-2">
                Quality Standards
              </h4>
              <p className="font-body text-sm text-technical-gray">
                All processes comply with AS9100 Rev D, NADCAP accreditation, and customer-specific quality requirements. Full traceability and documentation provided.
              </p>
            </div>
            <div>
              <h4 className="font-headline text-sm font-semibold text-foreground mb-2">
                Lead Time
              </h4>
              <p className="font-body text-sm text-technical-gray">
                Standard lead times range from 2-6 weeks depending on complexity. Expedited processing available for mission-critical requirements.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapabilityCard;